/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The cloud provider of the credential.
 */
export const AwsIamCredentialProvider = {
  AwsIam: "aws_iam",
  GcpIam: "gcp_iam",
} as const;
/**
 * The cloud provider of the credential.
 */
export type AwsIamCredentialProvider = ClosedEnum<
  typeof AwsIamCredentialProvider
>;

/**
 * An AWS IAM external credential.
 */
export type AwsIamCredential = {
  /**
   * The customer IAM role ARN Speakeasy assumes.
   */
  assumeRoleArn?: string | undefined;
  /**
   * When the credential was created.
   */
  createdAt: Date;
  /**
   * The Speakeasy-generated ExternalId the customer must require in their role trust policy. Present when Speakeasy assumes the role with an ExternalId.
   */
  externalId?: string | undefined;
  /**
   * The ID of the external credential.
   */
  id: string;
  /**
   * A human-readable name for the credential.
   */
  name: string;
  /**
   * The OIDC audience. Present when Speakeasy assumes the role with a web identity.
   */
  oidcAudience?: string | undefined;
  /**
   * Optional OIDC subject pin (web-identity approach).
   */
  oidcSubject?: string | undefined;
  /**
   * The organization that owns the credential.
   */
  organizationId: string;
  /**
   * The cloud provider of the credential.
   */
  provider: AwsIamCredentialProvider;
  /**
   * Optional STS region override.
   */
  stsRegion?: string | undefined;
  /**
   * When the credential was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const AwsIamCredentialProvider$inboundSchema: z.ZodMiniEnum<
  typeof AwsIamCredentialProvider
> = z.enum(AwsIamCredentialProvider);

/** @internal */
export const AwsIamCredential$inboundSchema: z.ZodMiniType<
  AwsIamCredential,
  unknown
> = z.pipe(
  z.object({
    assume_role_arn: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    external_id: z.optional(z.string()),
    id: z.string(),
    name: z.string(),
    oidc_audience: z.optional(z.string()),
    oidc_subject: z.optional(z.string()),
    organization_id: z.string(),
    provider: AwsIamCredentialProvider$inboundSchema,
    sts_region: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "assume_role_arn": "assumeRoleArn",
      "created_at": "createdAt",
      "external_id": "externalId",
      "oidc_audience": "oidcAudience",
      "oidc_subject": "oidcSubject",
      "organization_id": "organizationId",
      "sts_region": "stsRegion",
      "updated_at": "updatedAt",
    });
  }),
);

export function awsIamCredentialFromJSON(
  jsonString: string,
): SafeParseResult<AwsIamCredential, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AwsIamCredential$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AwsIamCredential' from JSON`,
  );
}
